import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Palette, Users, Heart, Lightbulb } from 'lucide-react';

const AboutSection = () => {
  const [ref, inView] = useInView({
    threshold: 0,
    triggerOnce: true,
    rootMargin: '50px 0px', // Start animation when component is 50px before entering viewport
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="section-padding bg-gradient-to-br from-dream-light-beige via-dream-pink-beige to-dream-soft-beige relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-20 right-10 w-40 h-40 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-20 left-10 w-32 h-32 bg-gradient-to-r from-dream-pink-beige to-dream-soft-beige rounded-full blur-2xl opacity-20"></div>
      </div>

      <motion.div
        ref={ref}
        className="container-width relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            מי מאחורי <span className="gradient-text">הקסם</span>?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            שתי נשים מחוננות שמביאות עשרות שנות ניסיון בהנחיית סדנאות והעצמה אישית
          </p>
        </motion.div>

        {/* Desktop layout with image between cards */}
        <div className="hidden md:grid md:grid-cols-[1fr_auto_1fr] gap-4 lg:gap-6 items-center mb-16">
          <motion.div
            variants={cardVariants}
            className="bg-gradient-to-br from-dream-pink-beige to-dream-soft-beige p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
          >
            <div className="flex items-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full flex items-center justify-center ml-4">
                <Palette className="text-white" size={32} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-800">גלית תמר</h3>
                <p className="text-dream-warm-brown font-semibold">מנחת סדנאות לוחות חזון ומטפלת באומנות</p>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              פסיכותרפיסטית באומנות, NLP יצירתי, דמיון מודרך, עם תואר B.Ed בחינוך מיוחד ואמנות. 
              מתמחה בסדנאות אישיות וקבוצתיות ומביאה עמה את הכלים והידע ליצירת חוויה 
              טרנספורמטיבית שמתחברת לחלומות ולחזון האישי של כל משתתף.
            </p>
            <a 
              href="https://www.galittamar.co.il/workshops/%d7%a1%d7%93%d7%a0%d7%aa-%d7%97%d7%96%d7%95%d7%9f-%d7%91%d7%90%d7%95%d7%96%d7%a0%d7%99%d7%95%d7%aa-%d7%94%d7%a8%d7%90%d7%a9%d7%95%d7%a0%d7%94-%d7%9e%d7%a1%d7%95%d7%92%d7%94-%d7%91%d7%99%d7%a9%d7%a8/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-dream-teal to-dream-warm-brown text-white px-4 py-2 rounded-full text-sm font-semibold hover:scale-105 transition-transform duration-300"
            >
              קראו עוד על גלית
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </motion.div>

          {/* Image between the two cards - desktop only */}
          <motion.div 
            variants={cardVariants}
            className="flex justify-center items-center h-full"
          >
            <div className="relative">
              <img 
                src="/images/niligalit.jpg"
                alt="גלית ונילי יחד"
                className="w-64 h-80 object-cover rounded-3xl shadow-xl border-6 border-white"
              />
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-dream-teal/10 to-transparent"></div>
            </div>
          </motion.div>

          <motion.div
            variants={cardVariants}
            className="bg-gradient-to-br from-dream-soft-beige to-dream-pink-beige p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
          >
            <div className="flex items-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-r from-dream-warm-brown to-dream-teal rounded-full flex items-center justify-center ml-4">
                <Lightbulb className="text-white" size={32} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-800">נילי שטול טראורינג</h3>
                <p className="text-dream-warm-brown font-semibold">הדרכה וליווי, יוצרת סדנאות להגשמה</p>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              מגיעה מרקע של הדרכה וליווי, יוצרת סדנאות להגשמה עם כלים לחיים. 
              נילי מתמחה בפיתוח כלים מעשיים שמעצימים אנשים להגשים את חלומותיהם 
              ולבנות את העתיד שהם חולמים עליו עם חוויה משנה חיים.
            </p>
            <a 
              href="https://lp.vp4.me/wj7y"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-dream-warm-brown to-dream-teal text-white px-4 py-2 rounded-full text-sm font-semibold hover:scale-105 transition-transform duration-300"
            >
              קראו עוד על נילי
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </motion.div>
        </div>

        {/* Mobile layout with cards first, then image */}
        <div className="md:hidden space-y-8 mb-16">
          <motion.div
            variants={cardVariants}
            className="bg-gradient-to-br from-dream-pink-beige to-dream-soft-beige p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
          >
            <div className="flex items-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full flex items-center justify-center ml-4">
                <Palette className="text-white" size={32} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-800">גלית תמר</h3>
                <p className="text-dream-warm-brown font-semibold">מנחת סדנאות לוחות חזון ומטפלת באומנות</p>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              פסיכותרפיסטית באומנות, NLP יצירתי, דמיון מודרך, עם תואר B.Ed בחינוך מיוחד ואמנות. 
              מתמחה בסדנאות אישיות וקבוצתיות ומביאה עמה את הכלים והידע ליצירת חוויה 
              טרנספורמטיבית שמתחברת לחלומות ולחזון האישי של כל משתתף.
            </p>
            <a 
              href="https://www.galittamar.co.il/workshops/%d7%a1%d7%93%d7%a0%d7%aa-%d7%97%d7%96%d7%95%d7%9f-%d7%91%d7%90%d7%95%d7%96%d7%a0%d7%99%d7%95%d7%aa-%d7%94%d7%a8%d7%90%d7%a9%d7%95%d7%a0%d7%94-%d7%9e%d7%a1%d7%95%d7%92%d7%94-%d7%91%d7%99%d7%a9%d7%a8/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-dream-teal to-dream-warm-brown text-white px-4 py-2 rounded-full text-sm font-semibold hover:scale-105 transition-transform duration-300"
            >
              קראו עוד על גלית
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </motion.div>

          <motion.div
            variants={cardVariants}
            className="bg-gradient-to-br from-dream-soft-beige to-dream-pink-beige p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
          >
            <div className="flex items-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-r from-dream-warm-brown to-dream-teal rounded-full flex items-center justify-center ml-4">
                <Lightbulb className="text-white" size={32} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-800">נילי שטול טראורינג</h3>
                <p className="text-dream-warm-brown font-semibold">הדרכה וליווי, יוצרת סדנאות להגשמה</p>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              מגיעה מרקע של הדרכה וליווי, יוצרת סדנאות להגשמה עם כלים לחיים. 
              נילי מתמחה בפיתוח כלים מעשיים שמעצימים אנשים להגשים את חלומותיהם 
              ולבנות את העתיד שהם חולמים עליו עם חוויה משנה חיים.
            </p>
            <a 
              href="https://lp.vp4.me/wj7y"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-dream-warm-brown to-dream-teal text-white px-4 py-2 rounded-full text-sm font-semibold hover:scale-105 transition-transform duration-300"
            >
              קראו עוד על נילי
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </motion.div>

          {/* Image after both cards - mobile only */}
          <motion.div 
            variants={cardVariants}
            className="flex justify-center"
          >
            <div className="relative">
              <img 
                src="/images/niligalit.jpg"
                alt="גלית ונילי יחד"
                className="w-64 h-64 object-cover rounded-3xl shadow-xl border-6 border-white"
              />
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-dream-teal/10 to-transparent"></div>
            </div>
          </motion.div>
        </div>

        <motion.div variants={itemVariants} className="text-center">
          <div className="bg-gradient-to-r from-dream-soft-beige to-dream-pink-beige p-8 rounded-3xl shadow-lg">
            {/* Workshop atmosphere image */}
            <div className="flex justify-center mb-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full flex items-center justify-center">
                  <Users className="text-white" size={24} />
                </div>
                <div className="w-12 h-12 bg-gradient-to-r from-dream-warm-brown to-dream-teal rounded-full flex items-center justify-center">
                  <Heart className="text-white" size={24} />
                </div>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">מה מיוחד בסדנה שלנו?</h3>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed mb-8">
              <span className="font-semibold text-dream-warm-brown">הסדנה היחידה בארץ מסוגה.</span> 
              {' '}חוויה בלתי נשכחת עם תפאורה קסומה וטכנולוגיה מתקדמת שיוצרת מסע אישי וקבוצתי יחד.
            </p>
            
            <div className="mb-8">
              <div className="relative w-full max-w-lg mx-auto">
                <img 
                  src="/images/WhatsApp Image 2025-08-11 at 10.16.19 (1).jpeg"
                  alt="אווירת הסדנה"
                  className="w-full h-64 object-cover rounded-2xl shadow-lg"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
              </div>
            </div>

            {/* Why Choose Us Points */}
            <div className="grid md:grid-cols-2 gap-6 text-right">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">✓</span>
                  </div>
                  <p className="text-gray-700">אנו מאמינות בכוח של הכלים הללו ומיישמות אותם בחיינו האישיים</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-dream-warm-brown to-dream-teal rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">✓</span>
                  </div>
                  <p className="text-gray-700">הסדנה מועברת בצורה ייחודית באמצעות אוזניות, עם תפאורה שמעוררת את הדמיון</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">✓</span>
                  </div>
                  <p className="text-gray-700">אנו מספקות אלמנטים חושיים לרוגע ולפתיחת השפע</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-dream-warm-brown to-dream-teal rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">✓</span>
                  </div>
                  <p className="text-gray-700">הסדנה מתאימה גם לפורומים אישיים וגם קבוצתיים, ומגיעה עד אליכם</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default AboutSection;
